import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Client } from "@shared/schema";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Search, MoreHorizontal, Phone, Mail, Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export function ClientList() {
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Define interface for API response
  interface ClientsResponse {
    clients: Client[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }
  }

  // Fetch clients from the API with pagination and caching
  const { data, isLoading, isError } = useQuery<ClientsResponse>({
    queryKey: ['/api/clients', { page, limit: pageSize, search: searchQuery }],
    queryFn: ({ queryKey }) => {
      const [path, params] = queryKey as [string, { page: number, limit: number, search: string }];
      let url = `${path}?page=${params.page}&limit=${params.limit}`;
      
      // Add search parameter if provided
      if (params.search) {
        url += `&search=${encodeURIComponent(params.search)}`;
      }
      
      return fetch(url).then(res => {
        if (!res.ok) throw new Error('Failed to fetch clients');
        return res.json();
      });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
  });
  
  // Extract clients and pagination data
  const clients = data?.clients || [];
  const totalCount = data?.pagination?.total || 0;
  const serverTotalPages = data?.pagination?.totalPages || 1;

  // Since we're passing the search query to the server API,
  // we don't need to filter clients here anymore
  const filteredClients = clients;

  // Use the server's pagination information
  const totalPages = serverTotalPages;

  // Server has already paginated the results
  const paginatedClients = filteredClients;
  
  // Handle page changes - updates the page state which triggers a new query
  function handlePageChange(newPage: number) {
    setPage(newPage);
    // If not searching, scroll to top since we're loading new data
    if (!searchQuery) {
      window.scrollTo(0, 0);
    }
  }
  
  function getInitials(firstName: string, lastName: string) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  
  // Navigate to client detail page
  function viewClient(clientId: number) {
    setLocation(`/clients-old/${clientId}`);
  }

  return (
    <div className="py-8 max-w-[1200px] mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-brand-orange-primary to-brand-orange-600 bg-clip-text text-transparent">Clients</h1>
        <div className="flex gap-3">
          <Button
            onClick={() => setLocation("/clients-old/new")}
            className="bg-gradient-to-r from-brand-orange-primary to-brand-orange-600 hover:from-brand-orange-600 hover:to-brand-orange-700 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Client
          </Button>
        </div>
      </div>
      
      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle>Quick Search</CardTitle>
          <CardDescription>
            Search for clients by name, email, phone, city, company name, or job title
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search clients..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1); // Reset to first page on new search
                }}
              />
            </div>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(parseInt(value));
                setPage(1); // Reset to first page when changing page size
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Rows per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 per page</SelectItem>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="20">20 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">Contact Info</TableHead>
                <TableHead className="hidden md:table-cell">Location</TableHead>
                <TableHead className="hidden md:table-cell">Company</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8 rounded-md" />
                    </TableCell>
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    Error loading clients. Please try again later.
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    {searchQuery
                      ? `No clients found matching "${searchQuery}"`
                      : "No clients found. Create your first client by clicking the 'New Client' button."}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedClients.map((client) => (
                  <TableRow
                    key={client.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => viewClient(client.id)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="bg-gradient-to-r from-brand-orange-primary to-brand-orange-600 text-white">
                          {client.avatarUrl ? (
                            <AvatarImage src={client.avatarUrl} alt={`${client.firstName} ${client.lastName}`} />
                          ) : null}
                          <AvatarFallback>
                            {getInitials(client.firstName, client.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {client.firstName} {client.lastName}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {client.city && client.country
                              ? `${client.city}, ${client.country}`
                              : client.city || client.country || ""}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="space-y-1">
                        {client.email && (
                          <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{client.email}</span>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{client.phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-gray-700 dark:text-gray-300">
                      {client.city ? client.city : "—"}
                      {client.state && `, ${client.state}`}
                      {client.country && !client.city && !client.state && client.country}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {/* Check if company info exists in any form */}
                      {(client as any).companyInfo?.companyName ||
                       (client as any).companyName ||
                       (client as any).companyInfo?.jobTitle ||
                       (client as any).jobTitle ? (
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>
                            {(client as any).companyInfo?.companyName ||
                             (client as any).companyName || ""}

                            {((client as any).companyInfo?.jobTitle || (client as any).jobTitle) &&
                              ((client as any).companyInfo?.companyName || (client as any).companyName ?
                                ` • ${(client as any).companyInfo?.jobTitle || (client as any).jobTitle}` :
                                (client as any).companyInfo?.jobTitle || (client as any).jobTitle)}
                          </span>
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            viewClient(client.id);
                          }}>
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            setLocation(`/clients-old/${client.id}/edit`);
                          }}>
                            Edit client
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            setLocation(`/trips/new?clientId=${client.id}`);
                          }}>
                            Create trip
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {totalPages > 1 && (
            <div className="py-4 border-t border-gray-200 dark:border-gray-700">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        if (page > 1) handlePageChange(page - 1);
                      }}
                      className={page === 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNumber = i + 1;
                    // Show first page, last page, current page, and pages around current page
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= page - 1 && pageNumber <= page + 1)
                    ) {
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(pageNumber);
                            }}
                            isActive={pageNumber === page}
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    }
                    
                    // Show ellipsis for gaps
                    if (
                      (pageNumber === 2 && page > 3) ||
                      (pageNumber === totalPages - 1 && page < totalPages - 2)
                    ) {
                      return (
                        <PaginationItem key={pageNumber}>
                          <span className="px-4">...</span>
                        </PaginationItem>
                      );
                    }
                    
                    return null;
                  })}
                  
                  <PaginationItem>
                    <PaginationNext 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        if (page < totalPages) handlePageChange(page + 1);
                      }}
                      className={page === totalPages ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
